import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from 'Services/APIService';

export interface FeedbackComplaint {
  id?: string;
  date_of_feedback?: string;
  feedback_provider_name?: string;
  contact_info?: string;
  type_of_feedback?: string;
  description?: string;
  action_taken?: string;
  resolved?: boolean;
  created_by?: string;
  created_at?: string;
}

export interface MaintenanceRequest {
  id?: string;
  name?: string;
  role?: string;
  description?: string;
  location_of_issue?: string;
  urgency_level?: string;
  date_of_request?: string;
  photo?: string;
  notes?: string;
  action_taken?: string;
  created_by?: string;
  created_at?: string;
}

export interface OutreachForm {
  id?: string;
  date_of_outreach?: string;
  client_name?: string;
  support_worker_name?: string;
  type_of_outreach?: string;
  description?: string;
  goals?: string;
  successful?: string;
  explanation?: string;
  sw_name?: string;
  sw_signature?: string;
  date_completed?: string;
  client_email?: string;
  notes?: string;
  action_taken?: string;
  created_by?: string;
  created_at?: string;
  resolved?: string;
}

export interface FeedbackAndManagementListState {
  feedback_complaints: FeedbackComplaint[];
  maintenance_requests: MaintenanceRequest[];
  outreach_forms: OutreachForm[];
  loading: boolean;
  error: string | null;
}

const initialState: FeedbackAndManagementListState = {
  feedback_complaints: [],
  maintenance_requests: [],
  outreach_forms: [],
  loading: false,
  error: null
};

export const fetchFeedbacks = createAsyncThunk<
  {
    feedback_complaints: FeedbackComplaint[];
    maintenance_requests: MaintenanceRequest[];
    outreach_forms: OutreachForm[];
  },
  { client_email: string },
  { rejectValue: string }
>('feedbackAndManagementList/fetchFeedbacks', async ({ client_email }, { rejectWithValue }) => {
  try {
    const project = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
      projectId?: string;
    };
    const service = JSON.parse(localStorage.getItem('servicesState') || '{}') as {
      selectedServiceTitle?: string;
    };

    if (!project.projectId || !service.selectedServiceTitle) {
      return rejectWithValue('Missing project or service information in localStorage');
    }

    const response = await axiosInstance.post('/ndis/form/get-feedbacks', {
      group_id: project.projectId,
      service_id: service.selectedServiceTitle,
      client_email
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch feedback data');
  }
});

const feedbackAndManagementListSlice = createSlice({
  name: 'feedbackAndManagementList',
  initialState,
  reducers: {
    clearFeedbackState: (state) => {
      state.feedback_complaints = [];
      state.maintenance_requests = [];
      state.outreach_forms = [];
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeedbacks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeedbacks.fulfilled, (state, action) => {
        state.loading = false;
        state.feedback_complaints = action.payload.feedback_complaints;
        state.maintenance_requests = action.payload.maintenance_requests;
        state.outreach_forms = action.payload.outreach_forms;
        state.error = null;
      })
      .addCase(fetchFeedbacks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearFeedbackState } = feedbackAndManagementListSlice.actions;
export default feedbackAndManagementListSlice.reducer;
